/**
 * MarketNewsSection - News feed from the /market-pulse endpoint.
 *
 * Shows recent stock market news with ticker tags, timestamps, and source info.
 */

import { Newspaper, ExternalLink, Clock } from 'lucide-react';
import { TickerLogo } from '@/components/ui/TickerLogo';

export interface MarketNewsItem {
  title: string;
  url: string;
  ticker: string;
  published: string;
  site: string;
  snippet: string;
}

interface MarketNewsSectionProps {
  news: MarketNewsItem[];
  isLoading?: boolean;
  onTickerClick?: (ticker: string) => void;
}

function timeAgo(dateStr: string): string {
  if (!dateStr) return '';
  try {
    const diff = Date.now() - new Date(dateStr).getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  } catch {
    return '';
  }
}

export function MarketNewsSection({ news, isLoading, onTickerClick }: MarketNewsSectionProps) {
  if (isLoading) {
    return (
      <div className="rounded-xl border border-border bg-surface p-4">
        <div className="flex items-center gap-2 mb-3">
          <Newspaper className="w-4 h-4 text-primary" />
          <h3 className="text-sm font-medium text-text-primary">Market News</h3>
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="animate-pulse space-y-2">
              <div className="h-3 bg-surface-light rounded w-3/4" />
              <div className="h-2 bg-surface-light rounded w-1/2" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!news.length) {
    return (
      <div className="rounded-xl border border-border bg-surface p-4">
        <div className="flex items-center gap-2 mb-2">
          <Newspaper className="w-4 h-4 text-primary" />
          <h3 className="text-sm font-medium text-text-primary">Market News</h3>
        </div>
        <p className="text-xs text-text-muted">No market news available right now.</p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border bg-surface p-4">
      <div className="flex items-center gap-2 mb-3">
        <Newspaper className="w-4 h-4 text-primary" />
        <h3 className="text-sm font-medium text-text-primary">Market News</h3>
      </div>

      <div className="space-y-3 max-h-[400px] overflow-y-auto pr-1">
        {news.map((item, i) => (
          <a
            key={i}
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            className="block group p-2.5 -mx-1 rounded-lg hover:bg-surface-light/50 transition-colors"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium text-text-primary leading-relaxed group-hover:text-primary transition-colors line-clamp-2">
                  {item.title}
                </p>
                <div className="flex items-center gap-2 mt-1.5">
                  {item.ticker && (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onTickerClick?.(item.ticker);
                      }}
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-primary/10 text-primary text-[10px] font-mono font-medium hover:bg-primary/20 transition-colors"
                    >
                      <TickerLogo symbol={item.ticker} size="xs" />
                      {item.ticker}
                    </button>
                  )}
                  {item.site && (
                    <span className="text-[10px] text-text-muted">{item.site}</span>
                  )}
                  {item.published && (
                    <span className="flex items-center gap-0.5 text-[10px] text-text-muted">
                      <Clock className="w-2.5 h-2.5" />
                      {timeAgo(item.published)}
                    </span>
                  )}
                </div>
              </div>
              <ExternalLink className="w-3 h-3 text-text-muted opacity-0 group-hover:opacity-100 transition-opacity shrink-0 mt-0.5" />
            </div>
          </a>
        ))}
      </div>
    </div>
  );
}
